import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Globe, ThumbsUp, Copy, Plus, Loader2, ArrowLeft, Flame, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { encodeTemplate } from "@/lib/templateCode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function XIcon({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function shareLinks(item) {
  const url = `${window.location.origin}/gallery?t=${item.id}`;
  const text = `Setup de trading « ${item.name} » par ${item.author} sur ANATRADEX — votez et importez-le en 1 clic 📊`;
  return {
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=ANATRADEX,Trading,SMC`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  };
}

export default function TemplateGallery() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("t");
  const inApp = location.pathname.startsWith("/app");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("top");

  useEffect(() => {
    if (!loading && highlightId) {
      const el = document.querySelector(`[data-testid="gallery-card-${highlightId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, highlightId]);

  const load = async (s = sort) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gallery?sort=${s}`);
      setItems(r.data.items || []);
    } catch {
      toast.error("Impossible de charger la galerie");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(sort); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sort]);

  const vote = async (item) => {
    try {
      const r = await axios.post(`${API}/gallery/${item.id}/vote`);
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, votes: r.data.votes, voted_by_me: r.data.voted_by_me } : i));
    } catch { toast.error("Vote impossible"); }
  };

  const copyCode = async (item) => {
    try {
      await navigator.clipboard.writeText(encodeTemplate(item));
      toast.success("Code PTPL1 copié — importez-le depuis le menu Templates du graphique");
    } catch { toast.error("Copie impossible"); }
  };

  const addToMyTemplates = async (item) => {
    try {
      await axios.post(`${API}/templates`, { name: item.name, payload: item.payload });
      toast.success(`« ${item.name} » ajouté à vos templates — retrouvez-le dans le menu Templates du graphique`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible");
    }
  };

  return (
    <div className={inApp ? "p-6" : "min-h-screen bg-bg text-white px-6 py-8"}>
      <div className={inApp ? "" : "max-w-6xl mx-auto"}>
      {!inApp && (
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-6" data-testid="gallery-back-home">
          <ArrowLeft size={13} /> Retour à l&apos;accueil
        </Link>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2" data-testid="gallery-title">
            <Globe size={22} className="text-accent-blue" /> Galerie de Templates Communautaire
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Les setups graphiques partagés par les traders ANATRADEX — votez pour les meilleurs, utilisez-les en 1 clic.
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => setSort("top")}
            className={`chip text-xs flex items-center gap-1 ${sort === "top" ? "chip-up" : "hover:bg-white/5"}`}
            data-testid="gallery-sort-top"
          ><Flame size={11} /> Top votes</button>
          <button
            onClick={() => setSort("recent")}
            className={`chip text-xs flex items-center gap-1 ${sort === "recent" ? "chip-up" : "hover:bg-white/5"}`}
            data-testid="gallery-sort-recent"
          ><Clock size={11} /> Récents</button>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20 text-white/40"><Loader2 className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="panel p-10 text-center text-white/40 text-sm mt-4" data-testid="gallery-empty">
          Aucun template publié pour l&apos;instant. Soyez le premier : sauvegardez votre configuration
          dans le menu Templates du graphique, puis cliquez sur <Globe size={12} className="inline" /> pour la publier.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="gallery-grid">
          {items.map((item) => (
            <div key={item.id} className={`panel p-4 flex flex-col ${highlightId === item.id ? "ring-2 ring-accent-blue" : ""}`} data-testid={`gallery-card-${item.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{item.name}</div>
                  <div className="text-[10px] text-white/40">
                    par {item.author} · {item.indicators_count} indicateurs · {item.created_at ? new Date(item.created_at).toLocaleDateString("fr-FR") : ""}
                  </div>
                </div>
                <button
                  onClick={() => vote(item)}
                  className={`shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${item.voted_by_me ? "border-up/50 bg-up/15 text-up" : "border-white/10 text-white/60 hover:border-up/40 hover:text-up"}`}
                  data-testid={`gallery-vote-${item.id}`}
                >
                  <ThumbsUp size={11} /> {item.votes}
                </button>
              </div>
              {item.description && (
                <p className="text-xs text-white/55 mt-2 line-clamp-3">{item.description}</p>
              )}
              <div className="flex gap-1.5 mt-auto pt-3">
                <button
                  onClick={() => addToMyTemplates(item)}
                  className="flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-md bg-accent-blue/80 hover:bg-accent-blue"
                  data-testid={`gallery-use-${item.id}`}
                >
                  <Plus size={11} /> Utiliser
                </button>
                <button
                  onClick={() => copyCode(item)}
                  className="flex items-center justify-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md border border-white/10 hover:border-accent-blue text-white/70"
                  title="Copier le code PTPL1"
                  data-testid={`gallery-copy-${item.id}`}
                >
                  <Copy size={11} /> Code
                </button>
                <a
                  href={shareLinks(item).x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-2.5 py-1.5 rounded-md border border-white/10 hover:border-white/40 text-white/60 hover:text-white"
                  title="Partager sur X"
                  data-testid={`gallery-share-x-${item.id}`}
                >
                  <XIcon size={11} />
                </a>
                <a
                  href={shareLinks(item).telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-2.5 py-1.5 rounded-md border border-white/10 hover:border-sky-400/60 text-white/60 hover:text-sky-400"
                  title="Partager sur Telegram"
                  data-testid={`gallery-share-tg-${item.id}`}
                >
                  <Send size={11} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-white/35 mt-6 leading-relaxed">
        Les templates ne contiennent que des préférences visuelles (thème, indicateurs, styles de zones) —
        aucun conseil en investissement. {!inApp && (<>Pas encore de compte ? <Link to="/signup" className="text-accent-blue underline">Essayez ANATRADEX gratuitement</Link>.</>)}
      </div>
      </div>
    </div>
  );
}
